import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatSidenav } from '@angular/material/sidenav';
import { NgxSpinnerService } from 'ngx-spinner';
import { PreviousPaymentsService, ScheduledPaymentsService } from '@aiwyn/services';
import { defer, EMPTY, iif, Observable, of, Subject } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PaymentsApiService, PaymentUi, StripeApiService } from '@aiwyn/api';
import { ConfigService, GetBusinessEntities, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { Store } from '@ngxs/store';
import { GetPaymentPlans, SettingsState } from '../settings/state';
import { GetAutoPayClients } from '@aiwyn/common/settings';
import { MatDialog } from '@angular/material/dialog';
import { isEmpty } from '@aiwyn/shared';
import { PaymentDateService } from './services/payment-date.service';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss'],
  standalone: false,
})
export class PaymentsComponent implements OnInit {

  // Sort for material table.
  @ViewChild(MatSort) sort: MatSort;

  @ViewChild('sidenav') public sidenav: MatSidenav;

  private _unsubscribe$ = new Subject<void>();

  // ClientId used to store URL data.
  // Note: URL must use format specified in app-routing.module.ts.
  clientId: string = '';
  hideComponent = true

  // Search string. Search applies to both tables: unpaid and paid.
  searchString: string;

  // Hide search bar if there are no invoices to show.
  hideSearchBar: boolean;

  /**
   * Whether the Remitian-backed Tax Payments section should render.
   * Bound in the template via `*ngIf="isRemitianTaxPaymentsEnabled$ | async"`
   * so the section is fully torn down (and its NGXS dispatch suppressed) when
   * the LD flag is off.
   */
  isRemitianTaxPaymentsEnabled$: Observable<boolean> = this.launchDarklyService.getFeatureFlag(
    LaunchDarklyFlag.ENABLE_REMITIAN_TAX_PAYMENTS,
  );

  constructor(
    public paymentsApiService: PaymentsApiService,
    private previousPaymentsService: PreviousPaymentsService,
    public scheduledPaymentService: ScheduledPaymentsService,
    public dialog: MatDialog,
    public configService: ConfigService,
    private spinner: NgxSpinnerService,
    private stripeApiService: StripeApiService,
    private readonly store: Store,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {}

  ngOnInit() {
    this.store.dispatch(new GetBusinessEntities(defer(() => this.stripeApiService.getBusinessEntities())));

    this.spinner.show();
    this.paymentsApiService.getPreviousPayments('').pipe(
      tap(previousPayments => {
        this.previousPaymentsService.dataSourcePreviousPayments.data = previousPayments.map(payment => {
          if(payment.payment.failedFundsWithdrawnAt) {
            payment.payment.failedFundsWithdrawnAt = (<number[]>payment.payment.failedFundsWithdrawnAt.slice(0, 3)).join('-');
          }

          const formattedPayment = PaymentDateService.formatLocalDateTimePayment(payment);
          formattedPayment.paymentSource = this.getPaymentSource(payment);

          return formattedPayment;
        });
      }),
      tap(() => {
        this.hideSearchBar = this.getHideSearchBar();
        this.previousPaymentsService.previousPaymentsLoaded$.next();
        this.scheduledPaymentService.scheduledPaymentsLoaded$.next();
        this.hideComponent = false;
      }),
      catchError((error) => {
        console.error('Could Not Retrieve Payments Data', error);
        return of(0);
      }),
      finalize(() => this.spinner.hide()),
      takeUntil(this._unsubscribe$),
    ).subscribe();

    this.configService.configValuesLoaded$.pipe(
      filter(Boolean),
      switchMap(_ => iif(
        () => this.configService.paymentPlanV4Enabled,
        defer(() => this.store.dispatch([new GetAutoPayClients(), new GetPaymentPlans()])),
        defer(() => EMPTY)
      )),
      take(1),
      takeUntil(this._unsubscribe$)
    ).subscribe();
  }

  getPaymentSource(paymentUi: PaymentUi): string {
    if (!paymentUi.payment.isAutomatic || (paymentUi.prePayments?.length === 1 && isEmpty(paymentUi.invoices))) {
      return '';
    }

    if (isEmpty(paymentUi.invoices) && isEmpty(paymentUi.prePayments)) {
      return 'Recurring';
    }

    if (!isEmpty(paymentUi.autoDraftEngagementIds)) {
      return 'Scheduled Payment';
    }

    if (!isEmpty(paymentUi.invoices) || !isEmpty(paymentUi.prePayments)) {
      return 'AutoPay';
    }

    return '';
  }
  /**
   * Should we hide the search bar and the unpaid invoice counter?
   */
  getHideSearchBar(): boolean {
    return this.previousPaymentsService.dataSourcePreviousPayments?.data?.length === 0 && this.store.selectSnapshot(SettingsState.getScheduledPayments)?.length === 0;
  }

  /**
   * Destroy subscriptions.
   */
  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

}
